import CartProduct from "../../cart/main/CartProduct.js";
import * as CommonUtilities from "../../../common/utils/CommonUtils.js";

/* =============================================================================
   Basket rail — the basket, visible on the menu page while you browse.

   ⚠ This reuses the cart page's own <CartProduct> rather than re-rendering the
   lines itself. That is deliberate: CartProduct already handles regular items,
   combos, modifier-combos, special instructions, the surcharge line (PLU
   999999000, which gets no quantity control and no Remove) and skips the
   service-fee line (PLU 999999010). Re-implementing any of that here would give
   two renderers that drift. Only the surrounding chrome is new.

   ⚠ NO MONEY IS CALCULATED HERE. The figure shown is cartDetails.totalInc
   exactly as the server returned it — the same field the cart page's Total
   uses. The fee breakdown deliberately lives at checkout only, so there is one
   place where fee lines are composed, not two.

   ⚠ The rail is hidden below 1100px (see theme.css). On narrow screens the
   header's Basket button already does this job, and a 340px rail beside a
   menu would leave neither enough room.
   ============================================================================= */

export default function MenuCartRail(props) {

    const cart = props.cartDetails.cartDetails;
    const products = cart && cart.products ? cart.products : [];
    const isLoading = props.cartDetails.cartIsLoading;

    // Lines the customer can actually act on — the fee and surcharge rows are
    // rendered by CartProduct but are not "items" for the count.
    const itemCount = products.filter((p) =>
        p.productPLU !== 999999010 && p.productPLU !== 999999000).length;

    const money = (v) => `${CommonUtilities.StaticFields.CurrencySymbol}${(v || 0).toFixed(2)}`;
    const total = cart && cart.totalInc ? money(cart.totalInc) : money(0);

    /* When fees apply, totalInc is more than the lines add up to. Showing only
       the total makes that gap look like an error; showing only the items
       under-promises what the customer will actually pay. So show both, and
       only when they actually differ — two server fields compared, no fee
       composition. If the fees are ever switched off server-side this line
       disappears on its own. */
    const hasFees = cart && cart.subTotal !== undefined && cart.subTotal !== cart.totalInc;

    const lines = products.map((item) =>
        <CartProduct
            key={item.recID}
            data={item}
            cartError={props.cartError}
            handleQuantityChange={props.handleQuantityChange}
            handleRemoveProdFromCart={props.handleRemoveProdFromCart}
        />
    );

    let body;
    if (isLoading) {
        body =
            <div className="wo-rail2__skel" aria-hidden="true">
                <span className="wo-skel wo-skel--name"></span>
                <span className="wo-skel wo-skel--line"></span>
                <span className="wo-skel wo-skel--name"></span>
                <span className="wo-skel wo-skel--line"></span>
            </div>;
    } else if (itemCount === 0) {
        body =
            <div className="wo-rail2__empty">
                <span className="wo-rail2__emptyMark" aria-hidden="true">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 3h2l2.4 11.4a2 2 0 0 0 2 1.6h8.2a2 2 0 0 0 2-1.6L21 7H6" />
                        <circle cx="10" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" />
                    </svg>
                </span>
                <p className="wo-rail2__emptyHead">Your basket is empty</p>
                <p className="wo-rail2__emptySub">Add something from the menu and it will show up here.</p>
            </div>;
    } else {
        body = <div className="wo-rail2__lines">{lines}</div>;
    }

    return (
        <aside className="wo-rail2" aria-label="Your basket">
            <div className="wo-rail2__panel">
                <div className="wo-rail2__head">
                    <span className="wo-rail2__title">Your basket</span>
                    {itemCount > 0 &&
                        <span className="wo-rail2__count">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>}
                </div>

                {body}

                {itemCount > 0 &&
                    <div className="wo-rail2__foot">
                        {hasFees &&
                            <div className="wo-rail2__sub">
                                <span>Items</span>
                                <span>{money(cart.subTotal)}</span>
                            </div>}
                        <div className="wo-rail2__total">
                            <span>Total</span>
                            <span>{total}</span>
                        </div>
                        <button type="button" className="wo-rail2__go" onClick={props.handleViewCart}
                                disabled={props.cartError.ButtonsDisabled}>
                            Go to checkout
                        </button>
                        <p className="wo-rail2__note">
                            {hasFees ? 'Fee breakdown and pick up time at checkout.' : 'Pick up time is set at checkout.'}
                        </p>
                    </div>}
            </div>
        </aside>
    )
}
